import axios from 'axios';
import constants from '~/utils/constants';

import type { AxiosApiResponse } from '~/api/types';
import type {
    EditHistoryItem,
    GetEditHistoryResponse,
    GetMetricsResponseData
} from './types';

/**
 * Implementations of API methods under the `/editroutehistory` path
 *
 * @category API
 */
export class EditRouteHistoryApi {
    /**
     * Path of the endpoint
     */
    private static readonly path = '/editroutehistory';

    static getMetrics(
        clientId: string,
        schedulerId: string
    ): Promise<AxiosApiResponse<GetMetricsResponseData>> {
        if (!clientId) {
            return Promise.reject('Missing clientId');
        }
        if (!schedulerId) {
            return Promise.reject('Missing schedulerId');
        }
        const params = new URLSearchParams({
            schedulerId
        });
        return axios.get(`${this.path}/metrics`, {
            params,
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static getEditHistory(
        clientId: string,
        schedulerId: string,
        limit = 5,
        page = 0
    ): Promise<GetEditHistoryResponse> {
        if (!clientId) {
            return Promise.reject('Missing clientId');
        }
        if (!schedulerId) {
            return Promise.reject('Missing schedulerId');
        }

        const params = new URLSearchParams({
            limit: String(limit),
            page: String(page),
            status: 'undoable',
            scheduler_id: schedulerId
        });
        return axios.get(this.path, {
            params,
            headers: {
                [constants.requestHeaders.WISE_CLIENT_ID]: clientId
            }
        });
    }

    static async getAllEditHistory(
        clientId: string,
        schedulerId: string
    ): Promise<EditHistoryItem[]> {
        const PAGE_SIZE = 50;
        const allEdits: EditHistoryItem[] = [];
        let currentPage = 0;
        let total = Infinity;

        while (allEdits.length < total) {
            // eslint-disable-next-line no-await-in-loop
            const response = await this.getEditHistory(
                clientId,
                schedulerId,
                PAGE_SIZE,
                currentPage
            );

            const { data, meta } = response.data;

            if (data.length === 0) {
                break;
            }

            allEdits.push(...data);
            total =
                meta?.total ??
                (data.length === PAGE_SIZE ? Infinity : allEdits.length);
            currentPage += 1;
        }

        return allEdits;
    }
}
